/** @format */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { catchError, map, retry, take, timeout } from 'rxjs/operators';

import { isPlatformBrowser } from '@angular/common';
import { ProductList } from '@plp/data-access/interfaces';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import { Observable, throwError } from 'rxjs';
import { Params } from '@angular/router';
import { Store } from '@ngrx/store';
import { DEFAULT_PAGE_SIZE } from '@plp/data-access/constants';
import { setSnbAudience } from '@plp/data-access/state/product-list.action';
import {
	selectMlBypass,
	selectSelectedSort,
	selectSnbAudience
} from '@plp/data-access/state/product-list.selector';
import {
	Chain,
	Environment,
	ProductCatalogInput,
	StoreDetails
} from '@shared/data-access/interfaces';
import { HeaderLocationService, HttpService, StoreService } from '@shared/data-access/services';

import { selectPageType, selectUserZipCode } from '@state/data-access';

/**
 * /* eslint-disable radix
 *
 * @format
 */

@Injectable({
	providedIn: 'root'
})
export class ProductListApiService {
	readonly;
	constructor(
		private chain: Chain,
		private http: HttpClient,
		private httpService: HttpService,
		private environment: Environment,
		private store: Store,
		private storeService: StoreService,
		private headerLocationService: HeaderLocationService,
		@Inject(PLATFORM_ID) private platformId: string
	) {}

	/**
	 * @description takes filters out of the url and reformats the filter data for the state store and UI components can consume
	 * @example filterFacets=X_BRAND%3APUMA to this {x_brand:['Puma']}
	 */
	formatFiltersFromURL(rawFilterData: string): Record<string, string[]> {
		const filters = decodeURIComponent(rawFilterData);
		const filterInfoMap: Record<string, string[]> = {};

		if (filters) {
			const filterFacets = filters.split(';');

			filterFacets.forEach((facetInfo) => {
				const facets = facetInfo.split(':');

				if (facets.length === 2) {
					filterInfoMap[facets[0]] = facets[1].split(',');
				}
			});
		}

		return filterInfoMap;
	}

	setProductListParamDataForApi(
		selectedCategoryId: number | null,
		searchTerm: string | null,
		pageNumber = 0,
		selectedFilters = null,
		pageSize = DEFAULT_PAGE_SIZE,
		includeFulfillmentFacets = false,
		selectedStoreNumber = '',
		zipcode = ''
	): ProductCatalogInput {
		let snbAudience = '';
		const isFamilyPage = this.getPageType() === 'plp';
		const selectedSort = this.getSelectedSort();
		const mlBypass = this.getMlBypassDataForExactSearchInstead();

		this.store
			.select(selectSnbAudience)
			.pipe(take(1))
			.subscribe((snbAudienceVal) => {
				if (snbAudienceVal) {
					// get the snb-audience directly from the store
					// this is the typical path for subsequent page loads
					snbAudience = snbAudienceVal;
				} else if (isPlatformBrowser(this.platformId) && window['snbAudienceGlobal']) {
					// get the snb-audience from the window object and set it in the store
					// this is the typical path for SRLP page load
					snbAudience = window['snbAudienceGlobal'];
					this.store.dispatch(setSnbAudience({ snbAudience }));
				} else if (isFamilyPage) {
					// get the snb-audience from the request header and set it in the store
					// this is the typical path for the first time the user hits the PLP
					const snbAudienceHeaderVal =
						this.httpService.getRequestHeaderValueAndSetStateKey('snb-audience');
					if (snbAudienceHeaderVal) {
						snbAudience = snbAudienceHeaderVal;
						this.store.dispatch(setSnbAudience({ snbAudience }));
					}
				}
			});

		let dataFromStoreCookieString: StoreDetails;

		// looking for zipcode and store on the cookie, important for first load
		if (!selectedStoreNumber || !zipcode) {
			const storeCookieString = this.storeService.getSavedCustomerStore();
			dataFromStoreCookieString =
				this.storeService.getStoreDataFromStoreCookieString(storeCookieString);
		}

		const queryParams = {
			pageNumber,
			pageSize,
			selectedSort,
			selectedStore: selectedStoreNumber || dataFromStoreCookieString?.storeNumber || '',
			storeId: this.chain.storeId,
			zipcode: zipcode || dataFromStoreCookieString?.storeZip || '',
			isFamilyPage,
			mlBypass,
			snbAudience,
			includeFulfillmentFacets
		};

		if (selectedFilters !== null && Object.keys(selectedFilters).length) {
			queryParams['selectedFilters'] = selectedFilters;
			// if selectedFilters is true, check if selected filter Same Day Delivey, set delivery location zipcode
			if (selectedFilters.facetStore?.length > 0 && selectedFilters.facetStore[0] === 'SDD') {
				queryParams['zipcode'] = this.getUserZipCode();
			}
		}

		if (isFamilyPage) {
			const selectedCategory = `${this.chain.catalogIdSales}_${selectedCategoryId}`;
			queryParams['selectedCategory'] = selectedCategory;
		} else if (searchTerm) {
			queryParams['searchTerm'] = searchTerm;
		}

		// Limit zipcode to first 5 characters
		if (queryParams.zipcode?.length > 5) {
			queryParams.zipcode = queryParams.zipcode.slice(0, 5);
		}

		if (includeFulfillmentFacets && ['dsg', 'gg'].includes(this.chain.chainIdentifierAbbr)) {
			queryParams['sddZipcode'] = this.getUserZipCode() || '';
		}
		return queryParams;
	}

	getPageType(): string {
		let pageType = '';
		this.store
			.select(selectPageType)
			.pipe(take(1))
			.subscribe((res) => {
				pageType = res;
			});
		return pageType.toLowerCase();
	}
	getSelectedSort(): number {
		let selectedSort = 0;
		this.store
			.select(selectSelectedSort)
			.pipe(take(1))
			.subscribe((res) => (selectedSort = res.value));
		return selectedSort;
	}
	getMlBypassDataForExactSearchInstead(): boolean {
		let mlBypass = false;
		this.store
			.select(selectMlBypass)
			.pipe(take(1))
			.subscribe((value: boolean) => {
				mlBypass = value;
			});
		return mlBypass;
	}

	getUserZipCode(): string {
		let zipCode = '';
		this.store
			.select(selectUserZipCode)
			.pipe(take(1))
			.subscribe((code) => {
				if (code) {
					zipCode = code;
				} else {
					zipCode =
						this.headerLocationService.getDeliveryZipCookie() ||
						this.headerLocationService.getWhereaboutsCookie();
				}
			});
		return zipCode;
	}

	getProductListDataFromApi$(
		queryParams: ProductCatalogInput,
		retries = 0,
		showPinnedProducts = true
	): Observable<ProductList | null> {
		const url = this.environment.api.productList;
		const params = encodeURIComponent(JSON.stringify(queryParams));
		const requestUrl = `${url}v2/search?searchVO=${params}`;

		let httpHeaders = new HttpHeaders({
			['X-TIMEOUT']: '7500',
			['Content-Type']: MediaType.APPLICATION_JSON,
			channel: this.chain.chainIdentifierAbbr,
			['disable-pinning']: `${!showPinnedProducts}`
		});

		if (isPlatformBrowser(this.platformId) && !queryParams.isFamilyPage) {
			const poolCSwimlane: number | undefined =
				window._dsgTag?.customerLanes.length >= 3
					? window._dsgTag?.customerLanes[2]
					: undefined;
			httpHeaders = httpHeaders.set(
				'pool-c-swimlane',
				`${poolCSwimlane && poolCSwimlane >= 0 ? poolCSwimlane : 0}`
			);
		}

		if (url.includes('prodauth')) {
			httpHeaders = httpHeaders.set('preview', 'rule');
		}

		// Get Origin Host Header
		const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
		if (origin) {
			httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
		}

		const httpOptions = {
			headers: httpHeaders
		};

		return this.http.get<ProductList | null>(requestUrl, httpOptions).pipe(
			map((pl: ProductList | null) => {
				return pl;
			}),
			retry(retries),
			timeout(10000),
			catchError((error) => throwError(() => error))
		);
	}

	searchRequestParamValidator(queryParams: Params): string {
		const validSearchTypes = [
			'Keyword',
			'Category',
			'Manufacturer',
			'Suggested+Keyword',
			'Suggested Keyword',
			'History'
		];
		const validateParams = {
			catalogId: {
				validate: (value: string) => this.chain.catalogIdSales === value
			},
			storeId: {
				validate: (value: string) => this.chain.storeId === value
			},
			langId: { validate: (value: string) => '-1' === value },
			sType: { validate: (value: string) => 'SimpleSearch' === value },
			resultCatEntryType: { validate: (value: string) => '2' === value },
			showResultsPage: { validate: (value: string) => 'true' === value },
			fromPage: { validate: (value: string) => 'Search' === value },
			searchSource: { validate: (value: string) => 'Q' === value },
			pageView: { validate: (value: string) => '' === value },
			beginIndex: { validate: (value: string) => '0' === value },
			categoryId: { validate: (value: string) => '' === value },
			pageSize: {
				validate: (value: string) => parseInt(value, 10) >= 0 && parseInt(value, 10) <= 145
			},
			SearchType: { validate: (value: string) => validSearchTypes.includes(value) },
			DSGSearchType: { validate: (value: string) => validSearchTypes.includes(value) },
			advsearch: { validate: (value: string) => 'true' === value },
			selectedStore: {
				validate: (value: string) =>
					parseInt(value, 10) >= 0 && parseInt(value, 10) <= 999999
			}
		};

		const errorMessages = [];

		if (!queryParams.searchTerm) {
			errorMessages.push('searchTerm is required');
		}

		/* linter flagged this as an error: Consider using "forEach" instead of "map" as its return value is not being used here */
		Object.keys(validateParams).forEach((validParam) => {
			if (queryParams[validParam]) {
				const isValueValid = validateParams[validParam].validate(queryParams[validParam]);
				if (!isValueValid) {
					errorMessages.push(`${validParam} is invalid`);
				}
			}
		});
		return errorMessages.join('|');
	}
}
